import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Row } from '@/components/Row';
import { ServerErrors } from '@/components/ServerErrors';
import { Typography } from '@/components/Typography';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { getRepairDetail, trackingNumberForgotten } from '@/lib/service/api';
import type { ApiErrorWithType, WithHTMLProp } from '@/lib/types';
import { queryStringEncrypted } from '@/lib/util';
import { commonMessages } from '@/messages/common';
import { serviceMessages } from '@/messages/service';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { FormSearch } from '../FormSearch';
import { ForgottenRepairNumberModal } from './components/ForgottenRepairNumberModal/ForgottenRepairNumberModal';
import type { ForgotRepairNumberSchema } from './components/ForgottenRepairNumberModal/schema';
import { RepairOrderNotFoundModal } from './components/RepairOrderNotFoundModal/RepairOrderNotFoundModal';
import type { ServiceStatusSchema } from './schema';
import { serviceStatusSchema } from './schema';

import { ReactComponent as SuccessIcon } from '@/assets/icons/checkbox-icon.svg';
import { ReactComponent as ErrorIcon } from '@/assets/icons/error.svg';

import * as S from './ServiceStatus.styles';

export type ServiceStatusProps = ContentfulPlaceholderServiceFragment &
  WithHTMLProp<'className'>;

export const ServiceStatus = (props: ServiceStatusProps) => {
  const { ecsConfig } = useConfigProvider();
  const { country } = useRegionProvider();
  const [openRepairOrderNotFoundModal, setOpenRepairOrderNotFoundModal] =
    useState<boolean>();
  const [openForgottenRepairNumberModal, setOpenForgottenRepairNumberModal] =
    useState<boolean>();
  const [isProcessing, setIsProcessing] = useState<boolean>();
  const [sendSuccess, setSendSuccess] = useState<boolean>();
  const [sendFail, setSendFail] = useState<boolean>();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const [hasTrackingNumberField, setHasTrackingNumberField] = useState(false);
  const { formatMessage } = useIntl();
  const { push } = useRouter();
  const schema = serviceStatusSchema(formatMessage);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const { sendEvent } = useGoogleAnalytics();
  useEffect(() => {
    if (sendSuccess) {
      setTimeout(() => setSendSuccess(false), 3000);
    }
    if (sendFail) {
      setTimeout(() => setSendFail(false), 3000);
    }
  }, [sendSuccess, sendFail]);
  const errorHandler = genericErrorHandler({
    formatMessage,
    formErrorsHandler: formCtx.setError,
    apiErrorsHandler: (errors, errorType) =>
      setErrors(
        errors.map((error) => ({
          type: errorType,
          ...error,
        })),
      ),
    errorType: 'service',
  });
  const handleSubmit: SubmitHandler<ServiceStatusSchema> = async (props) => {
    try {
      setIsProcessing(true);
      const res = await getRepairDetail({
        clientEmail: props.email,
        code: props.repairNumber,
        countryCode: country.code,
      });
      if (res?.code === 200 && !res.errors?.length) {
        sendEvent({
          event: EVENT_NAME.SERVICE_STATUS,
        });
        if (res.data.items?.length) {
          await push(
            paths.service.details.asPath({
              params: queryStringEncrypted({
                code: props.repairNumber,
                email: props.email,
              }),
            }),
          );
        } else {
          setOpenRepairOrderNotFoundModal(true);
        }
      } else {
        errorHandler(
          res?.errors ?? [
            {
              code: 'error',
            },
          ],
        );
      }

      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      errorHandler([{ code: 'error' }]);
    }
  };
  const handleForgotRepairNumber = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpenForgottenRepairNumberModal(true);
  };
  const handleSubmitForgotRepairNumberForm: SubmitHandler<
    ForgotRepairNumberSchema
  > = async (props) => {
    try {
      setIsProcessing(true);
      const res = await trackingNumberForgotten({
        email: props.email,
        countryCode: country.code,
      });
      setHasTrackingNumberField(
        !!res && 'trackingNumberForgottenResult' in res,
      );
      if (res?.trackingNumberForgottenResult) {
        setSendSuccess(true);
      } else {
        setSendFail(true);
      }
      setOpenForgottenRepairNumberModal(false);
      setIsProcessing(false);
    } catch (error) {
      setOpenForgottenRepairNumberModal(false);
      setIsProcessing(false);
      setSendFail(true);
    }
  };

  if (!ecsConfig?.ecsAvailable) return null;
  return (
    <FormSearch
      bg='lightGrey'
      data-test-id='ServiceStatusFormSearch'
      {...props}
    >
      <FormProvider name='serviceStatus' {...formCtx} onSubmit={handleSubmit}>
        <S.FormWrapper data-test-id='ServiceStatusFormWrapper'>
          <ServerErrors
            data-test-id='ServiceStatusServerErrors'
            errors={errors}
          />
          {sendSuccess && (
            <Flex
              alignItems='center'
              bg='feedbackHighLight'
              data-testid='emailSentSuccess'
              p={1}
            >
              <SuccessIcon />
              <Typography ml={1}>
                {formatMessage(serviceMessages.emailSentSuccess)}
              </Typography>
            </Flex>
          )}
          {sendFail && (
            <Flex
              alignItems='center'
              bg='feedbackError'
              data-testid='emailSentFail'
              p={1}
            >
              <ErrorIcon />
              <Typography color='white' ml={1}>
                {formatMessage(
                  hasTrackingNumberField
                    ? serviceMessages.noRepairFoundWithEmail
                    : serviceMessages.emailSentFail,
                )}
              </Typography>
            </Flex>
          )}

          <InputField
            aria-label={formatMessage(serviceMessages.email)}
            data-test-id='ServiceStatusEmailInputField'
            name='email'
            placeholder={formatMessage(serviceMessages.email)}
            required
          />
          <InputField
            aria-label={formatMessage(serviceMessages.repairNumber)}
            data-test-id='ServiceStatusRepairNumberInputField'
            name='repairNumber'
            placeholder={formatMessage(serviceMessages.repairNumber)}
            required
          />
          <Row>
            <Col sm={6} smStart={4} xs={12} xsStart={1}>
              <Button
                data-test-id='ServiceStatusSubmitButton'
                disabled={isProcessing}
                isFullWidth
                isProcessing={isProcessing}
                type='submit'
              >
                {formatMessage(commonMessages.search)}
              </Button>
            </Col>
          </Row>
          <Typography
            color='primaryBlue'
            data-test-id='ServiceStatusTypography'
            textAlign='center'
          >
            <LinkWithLocale
              data-testid='ServiceStatusLink'
              href='#'
              onClick={handleForgotRepairNumber}
            >
              {formatMessage(serviceMessages.forgotRepairNumber)}
            </LinkWithLocale>
          </Typography>
        </S.FormWrapper>
      </FormProvider>
      <RepairOrderNotFoundModal
        isOpen={openRepairOrderNotFoundModal}
        onClose={() => setOpenRepairOrderNotFoundModal(false)}
        onHeaderClose={() => setOpenRepairOrderNotFoundModal(false)}
      ></RepairOrderNotFoundModal>
      <ForgottenRepairNumberModal
        isOpen={openForgottenRepairNumberModal}
        isProcessing={isProcessing}
        onClose={() => setOpenForgottenRepairNumberModal(false)}
        onHeaderClose={() => setOpenForgottenRepairNumberModal(false)}
        onSubmit={handleSubmitForgotRepairNumberForm}
      ></ForgottenRepairNumberModal>
    </FormSearch>
  );
};

ServiceStatus.displayName = 'ServiceStatus';
